import { useEffect, useRef } from "react";
import { sanitizeForEditor, sanitizePasteHtml } from "../utils/sanitize";

/**
 * Schlanker Rich-Text-Editor auf contentEditable-Basis.
 * Unterstützt: Fett, Kursiv, Textgrößen 8pt – 26pt, Ausrichtung.
 */
const SIZES = ["8pt", "10pt", "12pt", "14pt", "16pt", "18pt", "20pt", "22pt", "24pt", "26pt"];

export default function RichTextEditor({ value, onChange, placeholder }) {
  const editorRef = useRef(null);

  // Externen Wert nur setzen wenn Editor keinen Fokus hat.
  // WICHTIG: eingehende HTML wird IMMER durch Sanitize gelassen — verhindert dass externe Styles/Klassen den Editor kaputt machen.
  useEffect(() => {
    if (!editorRef.current) return;
    const cleaned = sanitizeForEditor(value || "");
    const currentHtml = editorRef.current.innerHTML;
    const hasFocus = document.activeElement === editorRef.current;
    if (!hasFocus && cleaned !== currentHtml) {
      editorRef.current.innerHTML = cleaned;
    }
  }, [value]);

  const emitChange = () => {
    if (!editorRef.current) return;
    if (onChange) onChange(editorRef.current.innerHTML);
  };

  const focusEditor = () => {
    if (editorRef.current) editorRef.current.focus();
  };

  const exec = (command, val = null) => {
    focusEditor();
    try { document.execCommand("styleWithCSS", false, true); } catch {}
    document.execCommand(command, false, val);
    emitChange();
  };

  /**
   * Wickelt die aktuelle Auswahl in ein <span style="font-size: Xpt"> ein.
   */
  const applySize = (pt) => {
    focusEditor();
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    if (range.collapsed) return;

    try { document.execCommand("styleWithCSS", false, true); } catch {}

    const span = document.createElement("span");
    span.style.fontSize = pt;
    try {
      const contents = range.extractContents();
      // vorhandene font-size spans in Selection entfernen
      contents.querySelectorAll?.("span[style*='font-size']").forEach((el) => {
        el.style.fontSize = "";
        if (!el.getAttribute("style")) el.removeAttribute("style");
      });
      contents.querySelectorAll?.("font[size]").forEach((el) => el.removeAttribute("size"));
      span.appendChild(contents);
      range.insertNode(span);

      const newRange = document.createRange();
      newRange.selectNodeContents(span);
      sel.removeAllRanges();
      sel.addRange(newRange);
    } catch (e) { /* fallback */ }
    emitChange();
  };

  /**
   * Paste-Handler: säubert Clipboard-HTML (Word, Google Docs, andere Websites)
   * bevor es in den Editor eingefügt wird.
   */
  const handlePaste = (e) => {
    e.preventDefault();
    const cd = e.clipboardData || window.clipboardData;
    if (!cd) return;
    const html = cd.getData("text/html");
    const text = cd.getData("text/plain") || "";
    let toInsert;
    if (html && html.trim()) {
      toInsert = sanitizePasteHtml(html);
    } else {
      // Nur Plain-Text — HTML-Entities escapen und Zeilenumbrüche zu <br>
      toInsert = text
        .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
        .replace(/\n/g, "<br>");
    }
    try { document.execCommand("insertHTML", false, toInsert); } catch {}
    emitChange();
  };

  const isEmpty = (value || "").replace(/<[^>]*>/g, "").trim() === "";

  return (
    <div className="rte">
      <div className="rte-toolbar" role="toolbar" aria-label="Formatierung">
        <button
          type="button"
          className="rte-btn rte-btn-bold"
          onMouseDown={(e) => { e.preventDefault(); exec("bold"); }}
          title="Fett (Ctrl+B)"
          data-testid="rte-bold"
        >
          <strong>F</strong>
        </button>
        <button
          type="button"
          className="rte-btn rte-btn-italic"
          onMouseDown={(e) => { e.preventDefault(); exec("italic"); }}
          title="Kursiv (Ctrl+I)"
          data-testid="rte-italic"
        >
          <em>K</em>
        </button>
        <span className="rte-divider" />
        <label className="rte-size-label" htmlFor="rte-size-select">Größe:</label>
        <select
          id="rte-size-select"
          className="rte-size"
          onChange={(e) => {
            const v = e.target.value;
            e.target.value = "";
            if (v) applySize(v);
          }}
          defaultValue=""
          data-testid="rte-size"
        >
          <option value="" disabled>wählen…</option>
          {SIZES.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <span className="rte-divider" />
        <button
          type="button"
          className="rte-btn"
          onMouseDown={(e) => { e.preventDefault(); try { document.execCommand("styleWithCSS", false, true); } catch {} exec("justifyLeft"); }}
          title="Links ausrichten"
          data-testid="rte-align-left"
          aria-label="Links ausrichten"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
            <rect x="1" y="2" width="14" height="2"/><rect x="1" y="6" width="9" height="2"/>
            <rect x="1" y="10" width="14" height="2"/><rect x="1" y="14" width="9" height="2"/>
          </svg>
        </button>
        <button
          type="button"
          className="rte-btn"
          onMouseDown={(e) => { e.preventDefault(); try { document.execCommand("styleWithCSS", false, true); } catch {} exec("justifyCenter"); }}
          title="Mitte ausrichten"
          data-testid="rte-align-center"
          aria-label="Mitte ausrichten"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
            <rect x="1" y="2" width="14" height="2"/><rect x="3.5" y="6" width="9" height="2"/>
            <rect x="1" y="10" width="14" height="2"/><rect x="3.5" y="14" width="9" height="2"/>
          </svg>
        </button>
        <button
          type="button"
          className="rte-btn"
          onMouseDown={(e) => { e.preventDefault(); try { document.execCommand("styleWithCSS", false, true); } catch {} exec("justifyRight"); }}
          title="Rechts ausrichten"
          data-testid="rte-align-right"
          aria-label="Rechts ausrichten"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
            <rect x="1" y="2" width="14" height="2"/><rect x="6" y="6" width="9" height="2"/>
            <rect x="1" y="10" width="14" height="2"/><rect x="6" y="14" width="9" height="2"/>
          </svg>
        </button>
        <span className="rte-divider" />
        <button
          type="button"
          className="rte-btn rte-btn-clear"
          onMouseDown={(e) => { e.preventDefault(); exec("removeFormat"); }}
          title="Formatierung entfernen"
          data-testid="rte-clear-format"
        >
          Formatierung entfernen
        </button>
      </div>

      <div
        ref={editorRef}
        className="rte-editor"
        contentEditable
        suppressContentEditableWarning
        onInput={emitChange}
        onBlur={emitChange}
        onPaste={handlePaste}
        role="textbox"
        aria-multiline="true"
        data-placeholder={placeholder || "Text eingeben…"}
        data-empty={isEmpty ? "true" : "false"}
        data-testid="rte-content"
      />
    </div>
  );
}
